import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import { Input } from 'components/FormFields';
import ModalV2 from 'components/Modal/ModalV2';
import Checkbox from 'components/v3/Checkbox';
import { useCreateApiKey } from '../../gql/useCreateApiKey';

// Create a partner API key, then show it once. The same modal moves from the
// form to the key; closing before Copy asks first, inside the modal.

const FORM_ID = 'create-api-key-form';

export const SCOPES = [
  { value: 'customers:read', label: 'Customers' },
  { value: 'sales:read', label: 'Sales' },
];

const ALL_SCOPES = SCOPES.map((scope) => scope.value);

// The scope list after one box changes, always in SCOPES order.
export const toggleScope = (current, scope, checked) =>
  ALL_SCOPES.filter((value) =>
    value === scope ? checked : current.includes(value)
  );

// The createApiKey input: the trimmed name, the ticked scopes, and
// expires_at (the end of the chosen day, UTC) only when a date was picked.
export const keyInput = (name, scopes, expiry) => {
  const input = { name: name.trim(), scopes };
  if (expiry) input.expires_at = `${expiry}T23:59:59.999Z`;
  return input;
};

const Fields = styled.div`
  display: grid;
  gap: 20px;
`;

const ScopeGroup = styled.fieldset`
  display: grid;
  gap: 8px;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

const ScopeLegend = styled.legend`
  padding: 0;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Mono = styled.code`
  font-family: ${({ theme }) => theme.fonts.monospace};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
`;

// Read-only by nature (not a field); one click selects the whole key.
const KeyBox = styled.code`
  display: block;
  padding: 12px 14px;
  font-family: ${({ theme }) => theme.fonts.monospace};
  font-size: 13px;
  line-height: 1.5;
  word-break: break-all;
  user-select: all;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background.overlay};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const CopyRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.secondary};

  button {
    width: auto;
  }
`;

const Warning = styled.p`
  margin: 16px 0 0;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => (theme.name === 'dark' ? '#FCD34D' : '#B45309')};
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(245, 158, 11, 0.15)'
      : 'rgba(245, 158, 11, 0.1)'};
`;

const Question = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textGray.secondary};
`;

const ScopeBoxes = ({ scopes, onChange }) => (
  <ScopeGroup>
    <ScopeLegend>Scopes</ScopeLegend>
    {SCOPES.map((scope) => (
      <Checkbox
        key={scope.value}
        boxed
        checked={scopes.includes(scope.value)}
        onChange={(checked) =>
          onChange(toggleScope(scopes, scope.value, checked))
        }
        label={
          <>
            {scope.label} <Mono>{scope.value}</Mono>
          </>
        }
      />
    ))}
  </ScopeGroup>
);

ScopeBoxes.propTypes = {
  scopes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

const FormFooter = ({ loading, onClose }) => (
  <>
    <ButtonV2 type="button" onClick={onClose} disabled={loading}>
      Cancel
    </ButtonV2>
    <ButtonV2 primary type="submit" form={FORM_ID} disabled={loading}>
      {loading ? 'Creating…' : 'Create'}
    </ButtonV2>
  </>
);

FormFooter.propTypes = {
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const KeyFormModal = ({ accountId, onClose, onError, onCreated }) => {
  const { createApiKey, loading } = useCreateApiKey(accountId);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [scopes, setScopes] = useState(ALL_SCOPES);
  const [expiry, setExpiry] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      setNameError('Give the key a name');
      return;
    }
    const result = await createApiKey(keyInput(name, scopes, expiry));
    if (result.success) onCreated(result.data.key);
    else onError(result.error);
  };

  const changeName = (event) => {
    setName(event.target.value);
    setNameError('');
  };

  return (
    <ModalV2
      isOpen
      onClose={onClose}
      closable={!loading}
      closeOnOverlayClick={!loading}
      title="Create API key"
      subtitle="The key lets a promoter's engineers pull this account's customers and sales."
      maxWidth="480px"
      footer={<FormFooter loading={loading} onClose={onClose} />}
    >
      <form id={FORM_ID} onSubmit={submit} noValidate>
        <Fields>
          <Input
            label="Name"
            aria-label="Name"
            required
            maxLength={100}
            placeholder="e.g. Acme ticketing sync"
            value={name}
            onChange={changeName}
            error={Boolean(nameError)}
            helperText={nameError || 'Tells this key apart in the list.'}
          />
          <ScopeBoxes scopes={scopes} onChange={setScopes} />
          <Input
            type="date"
            label="Expires (optional)"
            aria-label="Expires"
            min={new Date().toISOString().slice(0, 10)}
            value={expiry}
            onChange={(event) => setExpiry(event.target.value)}
            helperText="Leave empty for a key that never expires. A key stops working at the end of that day (UTC)."
          />
        </Fields>
      </form>
    </ModalV2>
  );
};

KeyFormModal.propTypes = {
  accountId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

const CloseCheck = ({ onBack, onClose }) => (
  <ModalV2
    isOpen
    onClose={onBack}
    title="Did you copy the key?"
    maxWidth="440px"
    footer={
      <>
        <ButtonV2 type="button" onClick={onBack}>
          Back to the key
        </ButtonV2>
        <ButtonV2 primary type="button" onClick={onClose}>
          Yes, close
        </ButtonV2>
      </>
    }
  >
    <Question>It will not be shown again.</Question>
  </ModalV2>
);

CloseCheck.propTypes = {
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const KeyRevealModal = ({ secret, onClose }) => {
  const boxRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [selected, setSelected] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // No clipboard API (or no permission): select the key so Ctrl+C copies it.
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch (error) {
      window.getSelection().selectAllChildren(boxRef.current);
      setSelected(true);
    }
  };

  const requestClose = () => (copied ? onClose() : setConfirming(true));

  if (confirming) {
    return <CloseCheck onBack={() => setConfirming(false)} onClose={onClose} />;
  }
  return (
    <ModalV2
      isOpen
      onClose={requestClose}
      title="Copy your key"
      maxWidth="520px"
      footer={
        <ButtonV2 primary type="button" onClick={requestClose}>
          Done
        </ButtonV2>
      }
    >
      <KeyBox ref={boxRef}>{secret}</KeyBox>
      <CopyRow>
        <ButtonV2 type="button" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </ButtonV2>
        {selected && <span>Selected: press Ctrl+C (⌘C on a Mac) to copy.</span>}
      </CopyRow>
      <Warning>
        This is the only time Pit Pay shows this key. Copy it now and keep it
        somewhere safe. If it is lost, revoke it and create a new one.
      </Warning>
    </ModalV2>
  );
};

KeyRevealModal.propTypes = {
  secret: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

const CreateApiKeyModal = ({ accountId, onClose, onError }) => {
  const [secret, setSecret] = useState(null);
  if (secret) return <KeyRevealModal secret={secret} onClose={onClose} />;
  return (
    <KeyFormModal
      accountId={accountId}
      onClose={onClose}
      onError={onError}
      onCreated={setSecret}
    />
  );
};

CreateApiKeyModal.propTypes = {
  accountId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default CreateApiKeyModal;
